import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, mensagemDoErro } from '../api';
import { data as fmtData, dataHora as fmtDataHora, numero as fmtNumero } from '../formato';
import {
  Area, Aviso, Campo, CampoNumerico, Carregando, Dialogo, ErroDaApi, Escolha,
  SegurarParaConfirmar, Selo, Vazio, abrirProtegido,
} from '../ui';
import type { Tom } from '../ui';
import { useSessao } from '../sessao';
import { useCaminho } from '../Caminho';

/**
 * Treinamentos e capacitações — o histórico do §17.
 *
 * «Também devemos ter um histórico de treinamentos/capacitações onde
 *  fica registrado o tema do treinamento, a carga horária, a ementa de
 *  forma resumida, o ministrante e os participantes (lista de presença).
 *  Ao lançar um treinamento deverá ser possível emitir um certificado
 *  [...] personalizado para cada participante, com a nota obtida no
 *  teste de aptidão.» — Humberto, 11/09/2026.
 *
 * Três telas num arquivo: a LISTA, a FICHA (onde se lança presença e
 * nota, se conclui e se emite o certificado) e o FORMULÁRIO de novo.
 *
 * O ciclo é curto: planejado → realizado → concluído. CONCLUIR é o ato
 * que decide o resultado de cada um, numera os certificados e concede
 * a competência dos métodos cobertos aos aprovados. Depois disso o
 * treinamento congela — corrigir é cancelar com motivo e lançar outro.
 */

interface Treinamento {
  id: string; codigo: string; tema: string; ementa: string | null;
  carga_horaria: string; data_inicio: string; data_fim: string;
  local: string | null; ministrante: string; ministrante_id: string | null;
  ministrante_nome: string | null; ministrante_qualificacao: string | null;
  ministrante_da_casa: boolean; tem_avaliacao: boolean; nota_minima: string;
  validade_meses: number | null; status: string; concluido_em: string | null;
  concluido_por_nome: string | null; cancelado_em: string | null;
  motivo_cancelamento: string | null; observacoes: string | null;
  participantes: number; presentes: number; aprovados: number;
  reprovados: number; metodos: number; metodos_codigos: string | null;
}
interface MetodoDoTreinamento {
  id: string; codigo: string; nome: string; exige_competencia: boolean;
}
interface Participante {
  id: string; funcionario_id: string; funcionario_nome: string;
  funcionario_cargo: string | null; funcionario_cpf: string | null;
  presente: boolean; nota: string | null; resultado: string | null;
  certificado_numero: string | null; observacoes: string | null;
}
/* Na ficha, `metodos` e `participantes` são as listas — na lista da
   tela anterior eram só as contagens da `vw_treinamento`. */
interface DocumentoEmitido {
  tipo: 'lista_presenca' | 'certificado'; participante_id: string | null;
  emitido_em: string; hash_sha256: string;
}
interface Ficha extends Omit<Treinamento, 'metodos' | 'participantes'> {
  metodos: MetodoDoTreinamento[]; participantes: Participante[];
  /** Os PDFs que a conclusão emitiu e guardou (onda 370). */
  documentos?: DocumentoEmitido[];
}
interface Funcionario { id: string; nome: string; cargo: string | null; cpf?: string | null }
interface Metodo { id: string; codigo: string; nome: string }

const STATUS: Record<string, string> = {
  planejado: 'Planejado', realizado: 'Realizado', concluido: 'Concluído',
  cancelado: 'Cancelado',
};
const TOM_STATUS: Record<string, Tom> = {
  planejado: 'neutro', realizado: 'andamento', concluido: 'ok',
  cancelado: 'critico',
};
const RESULTADO: Record<string, string> = {
  aprovado: 'Aprovado', reprovado: 'Reprovado', ausente: 'Ausente',
};
const TOM_RESULTADO: Record<string, Tom> = {
  aprovado: 'ok', reprovado: 'critico', ausente: 'neutro',
};

type Lugar = 'topo' | 'presenca' | 'encerrar';

const cpfBonito = (v: string | null | undefined) =>
  v ? v.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4') : '';

// ── A lista ─────────────────────────────────────────────────────────

export function Treinamentos() {
  const [linhas, setLinhas] = useState<Treinamento[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [status, setStatus] = useState<string | null>(null);
  const { pode } = useSessao();

  useEffect(() => {
    setErro(null);
    const q = status ? `?status=${status}` : '';
    api<Treinamento[]>(`/treinamentos${q}`).then(setLinhas).catch(setErro);
  }, [status]);

  return (
    <div>
      <div className="linha-topo">
        <div className="cresce">
          <h1>Treinamentos</h1>
          <p className="sub">
            O histórico de capacitações da casa. Concluir um treinamento
            concede a competência dos métodos cobertos a quem foi aprovado —
            e emite o certificado.
          </p>
        </div>
        <Link to="/competencias" className="botao">Competências</Link>
        {pode('competencia.conceder') && (
          <Link to="/treinamentos/novo" className="botao principal">
            Novo treinamento
          </Link>
        )}
      </div>

      <div className="cartao">
        <div className="linha-campos">
          <Escolha rotulo="Situação" valor={status} aoEscolher={setStatus}
            opcoes={Object.entries(STATUS).map(([valor, texto]) => ({
              valor, texto }))} />
        </div>
        {erro ? <ErroDaApi erro={erro} />
          : !linhas ? <Carregando />
          : linhas.length === 0 ? (
            <Vazio>
              Nenhum treinamento lançado. O treinamento existe antes da
              trava: é aqui que a competência nasce com evidência.
            </Vazio>
          ) : (
            <div className="rola">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Código</th>
                    <th scope="col">Tema</th>
                    <th scope="col">Quando</th>
                    <th scope="col">Ministrante</th>
                    <th scope="col">Carga</th>
                    <th scope="col">Presença</th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((t) => (
                    <tr key={t.id}>
                      <th scope="row">
                        <Link to={`/treinamentos/${t.id}`} className="mono">
                          {t.codigo}
                        </Link>
                      </th>
                      <td>
                        {t.tema}
                        {t.metodos_codigos && (
                          <span className="sub mono">{t.metodos_codigos}</span>
                        )}
                      </td>
                      <td>
                        {fmtData(t.data_inicio)}
                        {t.data_fim !== t.data_inicio && ` – ${fmtData(t.data_fim)}`}
                      </td>
                      <td>{t.ministrante}</td>
                      <td>{fmtNumero(t.carga_horaria, 1, true)} h</td>
                      <td>
                        {t.participantes} inscrito(s)
                        {t.status === 'concluido' && (
                          <span className="sub">
                            {t.aprovados} aprovado(s)
                            {t.reprovados > 0 && ` · ${t.reprovados} reprovado(s)`}
                          </span>
                        )}
                      </td>
                      <td>
                        <Selo tom={TOM_STATUS[t.status] ?? 'neutro'}>
                          {STATUS[t.status] ?? t.status}
                        </Selo>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
}

// ── O formulário (novo e edição) ────────────────────────────────────

interface Valores {
  tema: string; ementa: string; cargaHoraria: string; dataInicio: string;
  dataFim: string; local: string; ministranteId: string | null;
  ministranteNome: string; ministranteQualificacao: string;
  temAvaliacao: boolean; notaMinima: string; validadeMeses: string;
  observacoes: string; metodos: string[];
}

const vazio = (): Valores => ({
  tema: '', ementa: '', cargaHoraria: '', dataInicio: '', dataFim: '',
  local: '', ministranteId: null, ministranteNome: '',
  ministranteQualificacao: '', temAvaliacao: true, notaMinima: '7',
  validadeMeses: '', observacoes: '', metodos: [],
});

const deFicha = (t: Ficha): Valores => ({
  tema: t.tema, ementa: t.ementa ?? '', cargaHoraria: String(t.carga_horaria),
  dataInicio: t.data_inicio.slice(0, 10), dataFim: t.data_fim.slice(0, 10),
  local: t.local ?? '', ministranteId: t.ministrante_id,
  ministranteNome: t.ministrante_nome ?? '',
  ministranteQualificacao: t.ministrante_qualificacao ?? '',
  temAvaliacao: t.tem_avaliacao, notaMinima: String(t.nota_minima),
  validadeMeses: t.validade_meses ? String(t.validade_meses) : '',
  observacoes: t.observacoes ?? '', metodos: t.metodos.map((m) => m.id),
});

const numeroBr = (v: string): number | undefined => {
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) && v.trim() !== '' ? n : undefined;
};

function FormularioTreinamento({ inicial, aoGravar, aoCancelar, gravando }: {
  inicial: Valores;
  aoGravar(v: Valores): void;
  aoCancelar(): void;
  gravando: boolean;
}) {
  const [v, setV] = useState<Valores>(inicial);
  const [gente, setGente] = useState<Funcionario[]>([]);
  const [metodos, setMetodos] = useState<Metodo[]>([]);
  const [buscaMetodo, setBuscaMetodo] = useState('');
  const [deFora, setDeFora] = useState(!inicial.ministranteId && !!inicial.ministranteNome);

  useEffect(() => {
    api<Funcionario[]>('/funcionarios?status=ativo').then(setGente)
      .catch(() => setGente([]));
    api<Metodo[]>('/normas/metodos').then(setMetodos).catch(() => setMetodos([]));
  }, []);

  const m = (patch: Partial<Valores>) => setV((x) => ({ ...x, ...patch }));
  const metodosVisiveis = useMemo(() => {
    const q = buscaMetodo.trim().toLowerCase();
    return metodos.filter((x) => !q || x.codigo.toLowerCase().includes(q)
      || x.nome.toLowerCase().includes(q));
  }, [metodos, buscaMetodo]);

  const alternarMetodo = (id: string) => m({
    metodos: v.metodos.includes(id)
      ? v.metodos.filter((x) => x !== id) : [...v.metodos, id],
  });

  const valido = v.tema.trim().length >= 3 && v.dataInicio && v.dataFim
    && v.dataFim >= v.dataInicio && (numeroBr(v.cargaHoraria) ?? 0) > 0
    && (deFora ? v.ministranteNome.trim().length >= 3 : !!v.ministranteId);

  return (
    <div className="cartao">
      <Campo rotulo="Tema" type="text" value={v.tema} autoFocus
        onChange={(ev) => m({ tema: ev.target.value })}
        dica="É o que sai no certificado: «Ensaio de compactação — NBR 7182»" />
      <div className="linha-campos">
        <Campo rotulo="Início" type="date" value={v.dataInicio}
          onChange={(ev) => m({ dataInicio: ev.target.value,
            dataFim: v.dataFim || ev.target.value })} />
        <Campo rotulo="Fim" type="date" value={v.dataFim}
          onChange={(ev) => m({ dataFim: ev.target.value })} />
        <CampoNumerico rotulo="Carga horária (h)" value={v.cargaHoraria}
          onChange={(ev) => m({ cargaHoraria: ev.target.value })} />
        <Campo rotulo="Local" type="text" value={v.local}
          onChange={(ev) => m({ local: ev.target.value })}
          dica="Opcional" />
      </div>

      <div className="linha-campos">
        <div className="campo">
          <span className="rotulo">Ministrante</span>
          <span className="opcoes-marcar">
            <label>
              <input type="radio" name="minis" checked={!deFora}
                onChange={() => { setDeFora(false); m({ ministranteNome: '' }); }} />
              {' '}alguém da casa
            </label>
            <label>
              <input type="radio" name="minis" checked={deFora}
                onChange={() => { setDeFora(true); m({ ministranteId: null }); }} />
              {' '}de fora
            </label>
          </span>
        </div>
        {deFora ? (
          <>
            <Campo rotulo="Nome do ministrante" type="text" value={v.ministranteNome}
              onChange={(ev) => m({ ministranteNome: ev.target.value })} />
            <Campo rotulo="Qualificação" type="text" value={v.ministranteQualificacao}
              dica="Opcional: «Eng. Civil, M.Sc.», «Instrutor da fabricante»"
              onChange={(ev) => m({ ministranteQualificacao: ev.target.value })} />
          </>
        ) : (
          <label className="campo">
            <span className="rotulo">Quem</span>
            <select value={v.ministranteId ?? ''}
              onChange={(ev) => m({ ministranteId: ev.target.value || null })}>
              <option value="">— escolha —</option>
              {gente.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.cargo ? `${g.nome} — ${g.cargo}` : g.nome}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <Area rotulo="Ementa resumida" rows={4} value={v.ementa}
        dica="Vai no verso do certificado. Uma linha por assunto, ou um parágrafo curto."
        onChange={(ev) => m({ ementa: ev.target.value })} />

      <div className="linha-campos">
        <label className="campo campo-marcar">
          <span className="rotulo">Avaliação</span>
          <span className="opcoes-marcar">
            <label>
              <input type="checkbox" checked={v.temAvaliacao}
                onChange={(ev) => m({ temAvaliacao: ev.target.checked })} />
              {' '}tem teste de aptidão (nota 0–10)
            </label>
          </span>
        </label>
        {v.temAvaliacao && (
          <CampoNumerico rotulo="Nota mínima" value={v.notaMinima}
            dica="Padrão da casa: 7,0"
            onChange={(ev) => m({ notaMinima: ev.target.value })} />
        )}
        <CampoNumerico rotulo="Validade da competência (meses)" inteiro
          value={v.validadeMeses} dica="Em branco = sem prazo"
          onChange={(ev) => m({ validadeMeses: ev.target.value })} />
      </div>

      <div className="campo">
        <span className="rotulo">
          Métodos de ensaio cobertos
          {v.metodos.length > 0 && <span className="sub"> · {v.metodos.length} marcado(s)</span>}
        </span>
        <span className="dica">
          Ao concluir, cada aprovado ganha competência em TODOS estes
          métodos. Treinamento geral (segurança, SGQ) fica sem método —
          registra e certifica, mas não concede.
        </span>
        <input type="search" value={buscaMetodo} aria-label="filtrar métodos"
          placeholder="filtrar por código ou nome do método"
          onChange={(ev) => setBuscaMetodo(ev.target.value)} />
        <div className="lista-marcar">
          {metodosVisiveis.map((x) => (
            <label key={x.id} className={v.metodos.includes(x.id) ? 'marcado' : ''}>
              <input type="checkbox" checked={v.metodos.includes(x.id)}
                aria-label={`${x.codigo} — ${x.nome}`}
                onChange={() => alternarMetodo(x.id)} />
              <span className="mono">{x.codigo}</span>
              <span className="sub">{x.nome}</span>
            </label>
          ))}
          {metodosVisiveis.length === 0 && (
            <span className="nada">nenhum método com este filtro</span>
          )}
        </div>
      </div>

      <Area rotulo="Observações" rows={2} value={v.observacoes}
        onChange={(ev) => m({ observacoes: ev.target.value })} />

      <div className="acoes">
        <button type="button" onClick={aoCancelar}>Cancelar</button>
        <button type="button" className="principal"
          disabled={!valido || gravando}
          onClick={() => aoGravar(v)}>
          {gravando ? 'Gravando…' : 'Gravar'}
        </button>
      </div>
    </div>
  );
}

const paraCorpo = (v: Valores) => ({
  tema: v.tema.trim(), ementa: v.ementa.trim() || null,
  cargaHoraria: numeroBr(v.cargaHoraria),
  dataInicio: v.dataInicio, dataFim: v.dataFim, local: v.local.trim() || null,
  ministranteId: v.ministranteId,
  ministranteNome: v.ministranteId ? null : v.ministranteNome.trim(),
  ministranteQualificacao: v.ministranteQualificacao.trim() || null,
  temAvaliacao: v.temAvaliacao,
  notaMinima: numeroBr(v.notaMinima) ?? 7,
  validadeMeses: v.validadeMeses.trim() ? Number(v.validadeMeses) : null,
  observacoes: v.observacoes.trim() || null,
  metodos: v.metodos,
});

export function TreinamentoNovo() {
  const navegar = useNavigate();
  const [erro, setErro] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);

  const gravar = async (v: Valores) => {
    setGravando(true); setErro(null);
    try {
      const t = await api<Ficha>('/treinamentos', {
        metodo: 'POST', corpo: paraCorpo(v) });
      navegar(`/treinamentos/${t.id}`);
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setGravando(false); }
  };

  return (
    <div>
      <div className="linha-topo">
        <div className="cresce">
          <h1>Novo treinamento</h1>
          <p className="sub">
            Grave o cabeçalho; a lista de presença e as notas entram na
            ficha, depois.
          </p>
        </div>
      </div>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <FormularioTreinamento inicial={vazio()} gravando={gravando}
        aoGravar={(v) => { void gravar(v); }}
        aoCancelar={() => navegar('/treinamentos')} />
    </div>
  );
}

// ── A ficha ─────────────────────────────────────────────────────────

export function TreinamentoFicha() {
  const { id = '' } = useParams();
  const navegar = useNavigate();
  const [t, setT] = useState<Ficha | null>(null);
  useCaminho(t ? [{ rotulo: t.tema, codigo: t.codigo }] : null);
  const [erro, setErro] = useState<unknown>(null);
  /*
   * O aviso nasce ONDE a pessoa agiu (183). No teste de 11/09, o erro
   * do CPF repetido e a recusa do §15 apareciam só no topo — a mil
   * pixels do botão — e pareciam «não gravou».
   */
  const [aviso, setAviso] = useState<{ texto: string; onde: Lugar } | null>(null);
  const [editando, setEditando] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [incluindo, setIncluindo] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const { pode, eu } = useSessao();

  const carregar = useCallback(async () => {
    setErro(null);
    try { setT(await api<Ficha>(`/treinamentos/${id}`)); } catch (e) { setErro(e); }
  }, [id]);

  useEffect(() => { void carregar(); }, [carregar]);

  const aberto = t?.status === 'planejado' || t?.status === 'realizado';
  const podeGerir = pode('competencia.conceder');

  const chamar = async (fn: () => Promise<unknown>, onde: Lugar = 'topo') => {
    setGravando(true); setAviso(null);
    try { await fn(); await carregar(); } catch (e) {
      setAviso({ texto: mensagemDoErro(e), onde });
    } finally { setGravando(false); }
  };
  const avisoEm = (onde: Lugar) => (aviso && aviso.onde === onde
    ? <Aviso tipo="erro">{aviso.texto}</Aviso> : null);

  if (erro) return <ErroDaApi erro={erro} />;
  if (!t) return <Carregando />;

  const semNota = t.tem_avaliacao
    ? t.participantes.filter((p) => p.presente && p.nota === null) : [];
  /*
   * As duas recusas do servidor que dá para ver ANTES de segurar o
   * botão: quem conclui assina e não pode estar na lista (§15); e o
   * ministrante não é aluno de si mesmo.
   */
  const euNaLista = !!eu?.funcionarioId
    && t.participantes.some((p) => p.funcionario_id === eu.funcionarioId);
  const ministranteNaLista = !!t.ministrante_id
    && t.participantes.some((p) => p.funcionario_id === t.ministrante_id);
  const podeConcluir = !gravando && t.participantes.length > 0
    && semNota.length === 0 && !euNaLista && !ministranteNaLista;

  return (
    <div>
      <div className="linha-topo">
        <div className="cresce">
          <p className="sub"><Link to="/treinamentos">Treinamentos</Link></p>
          <h1><span className="mono">{t.codigo}</span> · {t.tema}</h1>
          <p className="sub">
            {fmtData(t.data_inicio)}
            {t.data_fim !== t.data_inicio && ` – ${fmtData(t.data_fim)}`}
            {' '}· {fmtNumero(t.carga_horaria, 1, true)} h
            {' '}· ministrado por {t.ministrante}
            {t.ministrante_qualificacao && ` (${t.ministrante_qualificacao})`}
            {t.local && ` · ${t.local}`}
          </p>
        </div>
        <Selo tom={TOM_STATUS[t.status] ?? 'neutro'}>
          {STATUS[t.status] ?? t.status}
        </Selo>
      </div>

      {avisoEm('topo')}

      {t.status === 'cancelado' && (
        <Aviso tipo="info" titulo={`Cancelado em ${fmtData(t.cancelado_em)}`}>
          {t.motivo_cancelamento}
        </Aviso>
      )}
      {t.status === 'concluido' && (
        <Aviso tipo="ok" titulo={`Concluído em ${fmtData(t.concluido_em)}${
          t.concluido_por_nome ? ` por ${t.concluido_por_nome}` : ''}`}>
          {t.aprovados} aprovado(s)
          {t.reprovados > 0 && `, ${t.reprovados} reprovado(s)`}
          {t.metodos.length > 0
            ? ` — competência concedida em ${t.metodos.length} método(s).`
            : ' — treinamento sem método vinculado: certifica, não concede.'}
          {' '}O registro está congelado; para corrigir, cancele com motivo
          (abaixo) e lance outro — cancelar revoga o que este treinamento
          concedeu.
        </Aviso>
      )}

      {editando ? (
        <FormularioTreinamento inicial={deFicha(t)} gravando={gravando}
          aoCancelar={() => setEditando(false)}
          aoGravar={(v) => {
            void chamar(async () => {
              await api(`/treinamentos/${t.id}`, {
                metodo: 'PATCH', corpo: paraCorpo(v) });
              setEditando(false);
            });
          }} />
      ) : (
        <div className="cartao">
          <div className="cartao-cabeca">
            <h2>O treinamento</h2>
            {aberto && podeGerir && (
              <button type="button" className="ligacao"
                onClick={() => setEditando(true)}>editar</button>
            )}
          </div>
          <dl className="ficha-dados">
            <dt>Ementa resumida</dt>
            <dd className="pre">{t.ementa || <span className="nada">não informada</span>}</dd>
            <dt>Métodos cobertos</dt>
            <dd>
              {t.metodos.length === 0
                ? <span className="nada">nenhum — treinamento geral</span>
                : t.metodos.map((m) => (
                  <span key={m.id} className="chip" title={m.nome}>
                    {m.codigo}{m.exige_competencia ? ' · trava' : ''}
                  </span>
                ))}
            </dd>
            <dt>Avaliação</dt>
            <dd>
              {t.tem_avaliacao
                ? `teste de aptidão, nota mínima ${fmtNumero(t.nota_minima, 1)}`
                : 'sem teste — presença basta'}
            </dd>
            <dt>Validade da competência</dt>
            <dd>{t.validade_meses ? `${t.validade_meses} meses` : 'sem prazo'}</dd>
            {t.observacoes && (<><dt>Observações</dt><dd>{t.observacoes}</dd></>)}
          </dl>
        </div>
      )}

      <div className="cartao">
        <div className="cartao-cabeca">
          <h2>Lista de presença</h2>
          <span className="sub">
            {t.participantes.length} inscrito(s) · {t.presentes} presente(s)
          </span>
          <ListaEmPapel t={t} />
          {aberto && podeGerir && (
            <button type="button" className="botao"
              onClick={() => setIncluindo(true)}>
              Incluir pessoas
            </button>
          )}
        </div>

        {avisoEm('presenca')}
        {t.participantes.length === 0 ? (
          <Vazio>Ninguém na lista ainda.</Vazio>
        ) : (
          <div className="rola">
            <table className="presenca">
              <thead>
                <tr>
                  <th scope="col">Pessoa</th>
                  <th scope="col">CPF</th>
                  <th scope="col">Presente</th>
                  {t.tem_avaliacao && <th scope="col">Nota</th>}
                  <th scope="col">Resultado</th>
                  <th scope="col">Certificado</th>
                </tr>
              </thead>
              <tbody>
                {t.participantes.map((p) => (
                  <LinhaPresenca key={p.id} p={p} t={t}
                    aberto={!!aberto && podeGerir} aoMudar={carregar} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {aberto && podeGerir && (
        <div className="cartao">
          <div className="cartao-cabeca"><h2>Encerrar</h2></div>
          {t.status === 'planejado' && (
            <p className="sub">
              Marque como <b>realizado</b> quando o treinamento acontecer;
              <b> concluir</b> é o passo final, depois de lançar presença e
              notas.
            </p>
          )}
          {semNota.length > 0 && (
            <Aviso tipo="atencao" titulo="Falta nota">
              {semNota.map((p) => p.funcionario_nome).join(', ')} — lance a
              nota ou desmarque a presença antes de concluir.
            </Aviso>
          )}
          {euNaLista && (
            <Aviso tipo="atencao" titulo="Você está na lista de presença">
              Quem conclui assina a concessão das competências e por isso não
              pode ser um dos treinados (§15). Peça a outra pessoa com a
              permissão de conceder que conclua — ou retire-se da lista, se
              entrou por engano.
            </Aviso>
          )}
          {ministranteNaLista && (
            <Aviso tipo="atencao" titulo="O ministrante está na lista">
              {t.ministrante} ministra este treinamento e não pode ser
              participante dele. Retire-o da lista.
            </Aviso>
          )}
          {t.participantes.some((p) => p.presente && !p.funcionario_cpf) && (
            <Aviso tipo="info" titulo="CPF em falta">
              O certificado leva o CPF. Quem está sem ele sai com «CPF não
              cadastrado» — preencha na lista acima antes de concluir.
            </Aviso>
          )}
          {avisoEm('encerrar')}
          <div className="acoes">
            <button type="button" onClick={() => setCancelando(true)}>
              Cancelar treinamento…
            </button>
            {t.status === 'planejado' && (
              <button type="button" disabled={gravando}
                onClick={() => { void chamar(() => api(`/treinamentos/${t.id}`, {
                  metodo: 'PATCH', corpo: { status: 'realizado' } }), 'encerrar'); }}>
                Marcar como realizado
              </button>
            )}
            <SegurarParaConfirmar tom="normal"
              texto={`Concluir e conceder (${t.presentes} presente(s))`}
              desabilitado={!podeConcluir}
              aoConfirmar={() => { void chamar(() => api(
                `/treinamentos/${t.id}/concluir`, { metodo: 'POST', corpo: {} }),
              'encerrar'); }} />
          </div>
          <p className="sub">
            Concluir decide o resultado de cada um, numera os certificados e
            concede a competência dos métodos cobertos aos aprovados. Na mesma
            hora a lista de presença e os certificados viram PDF e ficam
            guardados. Depois disso o treinamento congela.
          </p>
        </div>
      )}

      {t.status === 'concluido' && podeGerir && (
        <div className="cartao">
          <div className="cartao-cabeca"><h2>Corrigir</h2></div>
          <p className="sub">
            Concluído não se edita. Se a lista, as notas ou os métodos saíram
            errados, cancele com motivo: as competências que este treinamento
            concedeu são revogadas na hora, os certificados passam a sair
            com «CANCELADO» atravessado, e você lança outro treinamento com
            os dados certos.
          </p>
          {avisoEm('encerrar')}
          <div className="acoes">
            <button type="button" className="perigo"
              onClick={() => setCancelando(true)}>
              Cancelar treinamento…
            </button>
          </div>
        </div>
      )}

      {incluindo && (
        <DialogoIncluir
          ja={t.participantes.map((p) => p.funcionario_id)}
          aoFechar={() => setIncluindo(false)}
          aoPronto={(ids) => {
            setIncluindo(false);
            void chamar(() => api(`/treinamentos/${t.id}/participantes`, {
              metodo: 'POST', corpo: { funcionarios: ids } }), 'presenca');
          }} />
      )}
      {cancelando && (
        <DialogoCancelar
          aoFechar={() => setCancelando(false)}
          aoPronto={(motivo) => {
            setCancelando(false);
            void chamar(() => api(`/treinamentos/${t.id}/cancelar`, {
              metodo: 'POST', corpo: { motivo } }), 'encerrar');
          }} concluido={t.status === 'concluido'} />
      )}
      {t.status === 'cancelado' && (
        <p className="sub">
          <button type="button" className="ligacao"
            onClick={() => navegar('/treinamentos')}>voltar à lista</button>
        </p>
      )}
    </div>
  );
}

/**
 * Uma linha da presença: presente, nota, CPF — cada gesto grava na
 * hora. O CPF grava no FUNCIONÁRIO (é dele, não do treinamento), e a
 * linha diz quando falta porque é o certificado que vai sair sem ele.
 */
function LinhaPresenca({ p, t, aberto, aoMudar }: {
  p: Participante; t: Ficha; aberto: boolean; aoMudar(): Promise<void>;
}) {
  const [nota, setNota] = useState(p.nota === null ? '' : String(p.nota).replace('.', ','));
  const [cpf, setCpf] = useState(cpfBonito(p.funcionario_cpf));
  const [editandoCpf, setEditandoCpf] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  /* O erro fica NA LINHA — é dela, e é onde o olho está (183). */
  const [erro, setErro] = useState<string | null>(null);
  const aoErro = setErro;

  useEffect(() => {
    setNota(p.nota === null ? '' : String(p.nota).replace('.', ','));
  }, [p.nota]);

  const gravar = async (corpo: Record<string, unknown>) => {
    setOcupado(true); aoErro(null);
    try {
      await api(`/treinamentos/${t.id}/participantes/${p.id}`, {
        metodo: 'PATCH', corpo });
      await aoMudar();
    } catch (e) { aoErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  const gravarNota = () => {
    if (!aberto) return;
    const limpo = nota.trim();
    const n = limpo === '' ? null : Number(limpo.replace(',', '.'));
    if (n !== null && (!Number.isFinite(n) || n < 0 || n > 10)) {
      aoErro('A nota vai de 0 a 10.'); return;
    }
    const atual = p.nota === null ? null : Number(p.nota);
    if (n === atual) return;
    void gravar({ nota: n });
  };

  const gravarCpf = async () => {
    const digitos = cpf.replace(/\D/g, '');
    setOcupado(true); aoErro(null);
    try {
      await api(`/funcionarios/${p.funcionario_id}`, {
        metodo: 'PATCH', corpo: { cpf: digitos || null } });
      setEditandoCpf(false);
      await aoMudar();
    } catch (e) { aoErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  const retirar = async () => {
    setOcupado(true); aoErro(null);
    try {
      await api(`/treinamentos/${t.id}/participantes/${p.id}`, { metodo: 'DELETE' });
      await aoMudar();
    } catch (e) { aoErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  const podeCertificado = t.status === 'concluido' && p.resultado === 'aprovado';

  return (
    <>
    <tr className={p.presente ? '' : 'ausente'}>
      <th scope="row">
        <span className="nome">{p.funcionario_nome}</span>
        {p.funcionario_cargo && <span className="sub">{p.funcionario_cargo}</span>}
      </th>
      <td>
        {editandoCpf ? (
          <span className="linha-entrada">
            <input value={cpf} inputMode="numeric" placeholder="000.000.000-00"
              aria-label={`CPF de ${p.funcionario_nome}`} autoFocus size={14}
              onChange={(ev) => setCpf(ev.target.value)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter') void gravarCpf();
                if (ev.key === 'Escape') setEditandoCpf(false);
              }} />
            <button type="button" className="principal" disabled={ocupado}
              onClick={() => { void gravarCpf(); }}>Gravar</button>
            <button type="button" aria-label="desistir"
              onClick={() => setEditandoCpf(false)}>×</button>
          </span>
        ) : p.funcionario_cpf ? (
          <>
            <span className="mono inteiro">{cpfBonito(p.funcionario_cpf)}</span>
            {aberto && (
              <button type="button" className="ligacao"
                onClick={() => setEditandoCpf(true)}>corrigir</button>
            )}
          </>
        ) : (
          <>
            <span className="nada">sem CPF</span>
            {aberto && (
              <button type="button" className="ligacao"
                onClick={() => setEditandoCpf(true)}>preencher</button>
            )}
          </>
        )}
      </td>
      <td>
        <label className="caixa-grade" title={p.presente ? 'presente' : 'ausente'}>
          <input type="checkbox" checked={p.presente} disabled={!aberto || ocupado}
            aria-label={`${p.funcionario_nome} presente`}
            onChange={(ev) => { void gravar({ presente: ev.target.checked }); }} />
        </label>
      </td>
      {t.tem_avaliacao && (
        <td>
          {aberto ? (
            <input className="nota" value={nota} inputMode="decimal"
              placeholder="0–10" disabled={!p.presente || ocupado}
              aria-label={`nota de ${p.funcionario_nome}`}
              onChange={(ev) => setNota(ev.target.value)}
              onBlur={gravarNota}
              onKeyDown={(ev) => { if (ev.key === 'Enter') (ev.target as HTMLInputElement).blur(); }} />
          ) : (
            p.nota === null ? <span className="nada">—</span>
              : fmtNumero(p.nota, 1)
          )}
        </td>
      )}
      <td>
        {p.resultado ? (
          <Selo tom={TOM_RESULTADO[p.resultado] ?? 'neutro'}>
            {RESULTADO[p.resultado] ?? p.resultado}
          </Selo>
        ) : (
          <Previsto p={p} t={t} />
        )}
      </td>
      <td>
        {podeCertificado ? (
          <>
            <span className="mono inteiro">{p.certificado_numero}</span>
            <span className="acoes-linha">
              <button type="button" className="ligacao"
                onClick={() => { void abrirProtegido(
                  `/treinamentos/participantes/${p.id}/certificado`,
                  `certificado-${p.certificado_numero}.html`); }}>
                ver
              </button>
              <button type="button" className="ligacao"
                onClick={() => { void abrirProtegido(
                  `/treinamentos/participantes/${p.id}/certificado?pdf=1`,
                  `certificado-${p.certificado_numero}.pdf`); }}>
                PDF
              </button>
            </span>
          </>
        ) : t.status === 'concluido' ? (
          <span className="nada">não emitido</span>
        ) : (
          <span className="acoes-linha">
            <button type="button" className="ligacao"
              title="pré-visualização, com PRELIMINAR atravessado"
              onClick={() => { void abrirProtegido(
                `/treinamentos/participantes/${p.id}/certificado`,
                'certificado-preliminar.html'); }}>
              prévia
            </button>
            {aberto && (
              <button type="button" className="ligacao perigo" disabled={ocupado}
                onClick={() => { void retirar(); }}>retirar</button>
            )}
          </span>
        )}
      </td>
    </tr>
    {erro && (
      <tr className="linha-erro">
        <td colSpan={t.tem_avaliacao ? 6 : 5} role="alert">
          <span className="erro-linha">{erro}</span>
          <button type="button" className="ligacao" onClick={() => setErro(null)}>
            entendi
          </button>
        </td>
      </tr>
    )}
    </>
  );
}

/**
 * O que a conclusão vai decidir, dito antes — «a decidir» com a nota
 * já lançada parecia nota que não pegou (183). É previsão: a regra de
 * verdade é a `fn_treinamento_concluir`, e a nota mínima é a do
 * treinamento.
 */
function Previsto({ p, t }: { p: Participante; t: Ficha }) {
  if (!p.presente) return <span className="nada">ausente</span>;
  if (!t.tem_avaliacao) return <span className="previsto ok">aprovado ao concluir</span>;
  if (p.nota === null) return <span className="nada">sem nota</span>;
  const passa = Number(p.nota) >= Number(t.nota_minima);
  return passa
    ? <span className="previsto ok">aprovado ao concluir</span>
    : <span className="previsto erro">reprovado ao concluir</span>;
}

function DialogoIncluir({ ja, aoFechar, aoPronto }: {
  ja: string[]; aoFechar(): void; aoPronto(ids: string[]): void;
}) {
  const [gente, setGente] = useState<Funcionario[]>([]);
  const [busca, setBusca] = useState('');
  const [marcados, setMarcados] = useState<Set<string>>(new Set());

  useEffect(() => {
    api<Funcionario[]>('/funcionarios?status=ativo').then(setGente)
      .catch(() => setGente([]));
  }, []);

  const q = busca.trim().toLowerCase();
  const visiveis = gente.filter((g) => !ja.includes(g.id)
    && (!q || g.nome.toLowerCase().includes(q)
      || (g.cargo ?? '').toLowerCase().includes(q)));

  const alternar = (id: string) => setMarcados((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  return (
    <Dialogo aberto titulo="Incluir na lista de presença" aoFechar={aoFechar}>
      <Campo rotulo="Buscar" type="search" value={busca} autoFocus placeholder="nome ou cargo"
        onChange={(ev) => setBusca(ev.target.value)} />
      <div className="lista-marcar alta">
        {visiveis.map((g) => (
          <label key={g.id} className={marcados.has(g.id) ? 'marcado' : ''}>
            <input type="checkbox" checked={marcados.has(g.id)}
              aria-label={g.nome} onChange={() => alternar(g.id)} />
            <span>{g.nome}</span>
            <span className="sub">{g.cargo ?? ''}</span>
          </label>
        ))}
        {visiveis.length === 0 && (
          <span className="nada">ninguém a incluir com este filtro</span>
        )}
      </div>
      <div className="acoes">
        <button type="button" className="ligacao"
          onClick={() => setMarcados(new Set(visiveis.map((g) => g.id)))}>
          marcar todos os visíveis
        </button>
        <button type="button" onClick={aoFechar}>Cancelar</button>
        <button type="button" className="principal" disabled={marcados.size === 0}
          onClick={() => aoPronto([...marcados])}>
          Incluir {marcados.size || ''}
        </button>
      </div>
    </Dialogo>
  );
}

function DialogoCancelar({ aoFechar, aoPronto, concluido }: {
  aoFechar(): void; aoPronto(motivo: string): void; concluido: boolean;
}) {
  const [motivo, setMotivo] = useState('');
  return (
    <Dialogo aberto titulo="Cancelar treinamento" aoFechar={aoFechar}>
      <Area rotulo="Por quê?" rows={3} value={motivo} autoFocus
        placeholder="não aconteceu, lançado em duplicidade, dados errados…"
        onChange={(ev) => setMotivo(ev.target.value)} />
      <p className="sub">
        O registro não é apagado: fica cancelado, com data, nome e motivo.
        {concluido
          ? ' As competências que este treinamento concedeu são revogadas '
            + 'junto (com este motivo), e os certificados dele passam a sair '
            + 'com «CANCELADO» atravessado.'
          : ' Nada foi concedido ainda; a lista de presença fica como registro.'}
      </p>
      <div className="acoes">
        <button type="button" onClick={aoFechar}>Voltar</button>
        <button type="button" className="principal perigo"
          disabled={motivo.trim().length < 10}
          onClick={() => aoPronto(motivo.trim())}>
          Cancelar treinamento
        </button>
      </div>
    </Dialogo>
  );
}


/**
 * A lista de presença em papel (onda 370, RN-278) — ao lado do título do cartão.
 *
 * Concluído: o PDF é o emitido junto com os certificados (os bytes guardados), e a linha diz quando. Antes disso, só
 * a prévia, com PRELIMINAR atravessado; cancelado, com CANCELADO.
 */
function ListaEmPapel({ t }: { t: Ficha }) {
  const emitida = t.documentos?.find((d) => d.tipo === 'lista_presenca') ?? null;
  const base = `/treinamentos/${t.id}/lista-presenca`;
  const nome = `${t.codigo}-LP`;
  return (
    <span className="acoes-linha">
      {t.status === 'concluido' ? (
        <>
          <span className="sub">
            {emitida ? `PDF emitido em ${fmtDataHora(emitida.emitido_em)}` : 'PDF a emitir no primeiro pedido'}
          </span>
          <button type="button" className="ligacao"
            onClick={() => { void abrirProtegido(base, `${nome}-R00.html`); }}>
            ver
          </button>
          <button type="button" className="ligacao"
            onClick={() => { void abrirProtegido(`${base}?pdf=1`, `${nome}-R00.pdf`); }}>
            PDF
          </button>
        </>
      ) : (
        <button type="button" className="ligacao"
          title={t.status === 'cancelado'
            ? 'a lista do treinamento cancelado, com CANCELADO atravessado'
            : 'pré-visualização, com PRELIMINAR atravessado'}
          onClick={() => { void abrirProtegido(base, `${nome}-R00-PRELIMINAR.html`); }}>
          prévia da lista
        </button>
      )}
    </span>
  );
}
